/*
  Given a user has a valid account
  When the user types in the correct credentials
  They will be authenticated and redirected to the home page.
  When the user types in invalid credentials
  Then an error will appear on screen
  Given a user doesn't remember their password
  When they click "Forgot Password"
  Then a temporary password will be sent to their email address.
*/

//Packages
import React, { useState, useEffect } from "react";
import axios from "axios";
//Components
import { Link } from "react-router-dom";
//Material UI Components
import {
  Grid,
  Typography,
  TextField,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  FormControlLabel,
  AccordionDetails,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ExpandMore, Visibility, VisibilityOff } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
//API Route
import { Route } from "../../utils/config";
//Animation
import { motion } from "framer-motion";
//Assets
import stockImg from "../../assets/images/homeStock2.png";
import mobileBkg2 from "../../assets/images/mobileBkg2.png";

//Material UI Styles
const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: "-3px",
  },
  backgroundContainer: {
    position: "absolute",
    width: "100%",
    top: "250px",
    overflowY: "hidden",
    zIndex: -1,
    [theme.breakpoints.up("md")]: {
      top: "400px",
    },
  },
  background: {
    backgroundImage: `url(${mobileBkg2})`,
    backgroundPositionX: "35%",
    display: "block",
    position: "relative",
    height: "545px",
    width: "100%",
  },
  backgroundFlipped: {
    backgroundImage: `url(${mobileBkg2})`,
    backgroundPositionX: "35%",
    display: "block",
    position: "relative",
    height: "545px",
    width: "100%",
    transform: "scaleY(-1)",
    marginBottom: "-340px",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  button: {
    textDecoration: "none",
  },
  forgotPassLink: {
    color: "#000000",
    border: "none",
    background: "none",
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underline",
    },
  },
}));

export default function Login() {
  const styles = useStyles();

  const [userData, setUserData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const [error, setError] = useState("");

  //State to determine if the user has attempted to login
  //If the user has tried to login, the login button will start its
  //loading animation until the api either returns a success response or an error
  const [isLoginClicked, setIsLoginClicked] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Will need to figure out how to display to the user that their password
  //email has been sent. For now, it pops up with an alert.
  const forgotPassword = () => {
    axios
      .post(`${Route}/Accounts/ForgotPassword`, { email: userData.email })
      .then(window.alert("Temporary Password Email sent"))
      .catch((err) => {
        throw err;
      });
  };

  const login = (data) => {
    axios
      .post(`${Route}/Accounts/Authenticate`, data, {
        withCredentials: true,
      })

      .then((res) => {
        localStorage.setItem(
          "login",
          JSON.stringify({
            login: true,
            user: {
              firstName: res.data.firstName,
              lastName: res.data.lastName,
              email: res.data.email,
              id: res.data.id,
              partnerId: res.data.partnerId,
              role: res.data.role,
            },
            token: res.data.jwtToken,
            refreshToken: res.data.refreshToken,
          })
        );
        window.location.replace(`${window.location.pathname}/`);
      })
      .catch((err) => {
        setIsLoginClicked(false);

        setError("Invalid credentials");
        throw err;
      });
  };

  const handleChange = (event) => {
    setUserData({ ...userData, [event.target.name]: event.target.value });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.root}>
        <Grid container justifyContent="center">
          <div className={styles.backgroundContainer}>
            <div className={styles.background}></div>
            <div className={styles.backgroundFlipped}></div>
          </div>

          <Grid item md={7} mt={2} mb={4}>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                setIsLoginClicked(true);
                login(userData);
              }}
              zIndex="2"
            >
              <Grid container item justifyContent="center">
                <Grid
                  container
                  item
                  xs={10}
                  md={6}
                  justifyContent="center"
                  spacing={3}
                  mb={2}
                >
                  <Grid item xs={12}>
                    <Typography variant="h5" color="#052442" mt={3}>
                      Welcome Back
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      variant="outlined"
                      name="email"
                      type="email"
                      label="Email"
                      fullWidth
                      onChange={handleChange}
                    ></TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      variant="outlined"
                      name="password"
                      value={userData.password}
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      onChange={handleChange}
                      InputProps={{
                        // <-- This is where the toggle button is added.
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    ></TextField>
                  </Grid>

                  <Grid
                    container
                    item
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item fontSize="14px">
                      <button
                        className={styles.forgotPassLink}
                        onClick={(e) => {
                          e.preventDefault();
                          forgotPassword();
                        }}
                        type="button"
                      >
                        Forgot Password?
                      </button>
                    </Grid>
                    <Grid item>
                      <FormControlLabel
                        value="Remember Me"
                        control={<Checkbox />}
                        label={
                          <Typography fontSize="14px">Remember Me</Typography>
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      sx={{ width: 85 }}
                      loading={isLoginClicked}
                    >
                      Login
                    </LoadingButton>
                    {error ? error : ""}

                    <Link to="/join" className={styles.button}>
                      <Button variant="contained" color="secondary">
                        Join Us
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid container justifyContent="center" display={{ md: "none" }}>
            <Grid item xs={11} sm={8} mb={2}>
              {/* The client will need to provide the content of these accordions */}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="h6">Privacy</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Cras gravida bibendum dolor eu varius. Morbi fermentum velit
                    nisl, eget vehicula lorem sodales eget. Donec quis volutpat
                    orci. Sed ipsum felis, tristique id egestas et, convallis ac
                    velit. In consequat dolor libero, nec luctus orci rutrum
                    nec. Phasellus vel arcu sed nibh ornare accumsan. Vestibulum
                    in elementum erat. Interdum et malesuada fames ac ante ipsum
                    primis in faucibus. Aenean laoreet rhoncus ipsum eget
                    tempus. Praesent convallis fermentum sagittis.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={11} sm={8} mb={2}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography variant="h6">Safety</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">
                    Cras gravida bibendum dolor eu varius. Morbi fermentum velit
                    nisl, eget vehicula lorem sodales eget. Donec quis volutpat
                    orci. Sed ipsum felis, tristique id egestas et, convallis ac
                    velit. In consequat dolor libero, nec luctus orci rutrum
                    nec. Phasellus vel arcu sed nibh ornare accumsan. Vestibulum
                    in elementum erat. Interdum et malesuada fames ac ante ipsum
                    primis in faucibus. Aenean laoreet rhoncus ipsum eget
                    tempus. Praesent convallis fermentum sagittis.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <Grid item md={5} display={{ xs: "none", md: "block" }}>
            <img
              src={stockImg}
              className="stock-image"
              alt="Happy children smiling"
            ></img>
          </Grid>
        </Grid>
      </div>
    </motion.div>
  );
}
